import type { VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cn } from '@savvy-kit/lib/utils';
import { cva } from 'class-variance-authority';

import { Spinner } from '../spinner/spinner';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus:border-ring focus:ring-border-light focus:ring-2 active:opacity-60 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive  hover:cursor-pointer",
  {
    variants: {
      variant: {
        default: 'bg-primary text-light shadow-xs hover:bg-primary/90',
        secondary:
          'border shadow-xs bg-background text-dark dark:bg-card hover:bg-component-hover dark:hover:bg-component-hover',
        destructive: 'bg-destructive text-light shadow-xs hover:bg-destructive/90 focus:ring-destructive/20',
        ghost: 'hover:bg-component-hover',
        link: 'cursor-pointer text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'text-sm h-10 px-4 py-2 has-[>svg]:px-3',
        sm: 'text-xs h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
        /** @deprecated Use `default` size instead */
        lg: 'h-12 px-8 has-[>svg]:px-4 font-semibold',
        // The default shadcn combobox requests icon-xs
        'icon-xs':
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
      },
      iconPosition: {
        left: 'flex-row',
        right: 'flex-row-reverse',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      iconPosition: 'left',
    },
  },
);

export type ButtonProps = Omit<ButtonPrimitive.Props, 'className'> &
  VariantProps<typeof buttonVariants> & {
    className?: string;
    isLoading?: boolean;
    icon?: ReactNode;
    iconPosition?: 'left' | 'right';
  };

function Button({
  className,
  variant,
  size,
  iconPosition = 'left',
  isLoading = false,
  disabled = false,
  icon,
  children,
  ...props
}: ButtonProps) {
  return (
    <ButtonPrimitive
      data-slot="button"
      disabled={disabled || isLoading}
      className={cn(buttonVariants({ variant, size, iconPosition }), className)}
      {...props}
    >
      {isLoading && <Spinner className="text-disabled" />}
      {!isLoading && icon}
      {children}
    </ButtonPrimitive>
  );
}

export { Button, buttonVariants };
